import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ProjectFilesSection } from '../../components/projects/ProjectFilesSection';
import { ProjectFormFields } from '../../components/projects/ProjectFormFields';
import { ProjectWorkspacePanel, WorkspaceTabId } from '../../components/projects/ProjectWorkspacePanel';
import { PageCard } from '../../components/layout/page/PageCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  getProject,
  Project,
  ProjectUpsertPayload,
  updateProject,
} from '../../services/commercialProjectsApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  normalizeProjectPayload,
  PROJECT_STATUS_LABELS,
  projectToForm,
} from '../../utils/projectFormUtils';

type DetailTab = 'geral' | WorkspaceTabId;

const WORKSPACE_TABS: { id: DetailTab; label: string }[] = [
  { id: 'geral', label: 'Geral' },
  { id: 'stakeholders', label: 'Stakeholders' },
  { id: 'requisitos', label: 'Requisitos' },
  { id: 'roadmap', label: 'Roadmap' },
  { id: 'backlog', label: 'Backlog' },
  { id: 'riscos', label: 'Riscos' },
  { id: 'problemas', label: 'Problemas' },
  { id: 'mudancas', label: 'Mudanças' },
  { id: 'reunioes', label: 'Reuniões' },
];

export function ProjectDetailPage() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('commercial.projects');

  const [project, setProject] = useState<Project | null>(null);
  const [form, setForm] = useState<ProjectUpsertPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>('geral');

  async function loadProject(): Promise<void> {
    if (!projectId) return;
    setLoading(true);
    try {
      const result = await getProject(projectId);
      setProject(result);
      setForm(projectToForm(result));
    } catch (error) {
      setProject(null);
      setForm(null);
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar projeto.'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  function buildBackLink(): string {
    const params = new URLSearchParams();
    const companyId = searchParams.get('companyId') ?? project?.companyId;
    const contractId = searchParams.get('contractId') ?? project?.contractId;
    if (companyId) params.set('companyId', companyId);
    if (contractId) params.set('contractId', contractId);
    const query = params.toString();
    return `/commercial/projects${query ? `?${query}` : ''}`;
  }

  async function handleSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !projectId || !form || !project) return;

    setSaving(true);
    try {
      const payload = normalizeProjectPayload(form, project.contractId);
      const updated = await updateProject(projectId, payload);
      setProject(updated);
      setForm(projectToForm(updated));
      toast.success('Projeto atualizado.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar projeto.'));
    } finally {
      setSaving(false);
    }
  }

  if (!projectId) {
    return (
      <PageShell>
        <p className="text-secondary-500">Projeto inválido.</p>
      </PageShell>
    );
  }

  const statusLabel = project ? (PROJECT_STATUS_LABELS[project.status] ?? project.status) : null;

  return (
    <PageShell>
      <PageHeader
        label="Projeto"
        title={loading ? 'Carregando...' : project?.name ?? 'Não encontrado'}
        description={
          project
            ? `${project.code ? `${project.code} · ` : ''}${project.companyName} — ${project.contractCode}`
            : 'Detalhes e workspace do projeto.'
        }
        actions={(
          <Link to={buildBackLink()} className="erp-btn-secondary text-sm">
            Voltar
          </Link>
        )}
      />

      {loading ? (
        <LoadingSpinner label="Carregando projeto..." />
      ) : !project || !form ? (
        <p className="text-secondary-500">Projeto não encontrado.</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-secondary-600">
            <span>
              Status: <span className="font-medium text-secondary-800">{statusLabel}</span>
            </span>
            <span>·</span>
            <span>{project.contractTitle}</span>
          </div>

          <div className="mb-4 flex flex-wrap gap-1 border-b border-secondary-200 pb-2">
            {WORKSPACE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                  activeTab === tab.id
                    ? 'bg-secondary-800 text-white'
                    : 'text-secondary-600 hover:bg-secondary-100'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'geral' ? (
            <div className="space-y-4">
              <PageCard>
                <form onSubmit={(event) => void handleSave(event)}>
                  <ProjectFormFields form={form} canWrite={canWrite} onChange={setForm} />
                  {canWrite ? (
                    <FormActionBar
                      onCancel={() => setForm(projectToForm(project))}
                      cancelLabel="Cancelar"
                      className="erp-btn-actions mt-4"
                    >
                      <button type="submit" className="erp-btn-primary" disabled={saving}>
                        {saving ? 'Salvando...' : 'Salvar projeto'}
                      </button>
                    </FormActionBar>
                  ) : null}
                </form>
              </PageCard>
              <ProjectFilesSection
                projectId={project.id}
                canWrite={canWrite}
                title="Documentos"
                description="Arquivos do projeto, enviados por upload ou informados como link."
              />
            </div>
          ) : (
            <ProjectWorkspacePanel
              projectId={projectId}
              canWrite={canWrite}
              activeTab={activeTab}
            />
          )}
        </>
      )}
    </PageShell>
  );
}
